import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Accepted session replacements, not profile refreshes. Private screens can
  // discard drafts even when the replacement belongs to the same account.
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const sessionRevision = useRef(0);
  // A new screen may mount while logout is awaiting device/server cleanup.
  // Revision equality alone cannot make that newly captured work safe.
  const sessionWorkAllowed = useRef(true);

  const captureSession = () => {
    const revision = sessionRevision.current;
    return () => sessionWorkAllowed.current && revision === sessionRevision.current;
  };

  const requestAuthentication = async (url, payload, accept, isCurrent = () => true) => {
    const revision = ++sessionRevision.current;
    setLoading(false);
    const assertCurrent = () => {
      // Obsolete failures must not redirect a newer session either. The caller
      // can also fence a request whose initiating screen has been left.
      if (revision !== sessionRevision.current || !isCurrent()) {
        const error = new Error("Authentication request superseded");
        error.code = "AUTH_SUPERSEDED";
        throw error;
      }
    };
    let response;
    try {
      response = await api.post(url, payload);
    } catch (error) {
      assertCurrent();
      throw error;
    }
    assertCurrent();
    // Commit synchronously with the revision check. Returning data for another
    // await continuation leaves a microtask gap where logout can supersede us.
    return accept(response.data);
  };

  const establishSession = (data) => {
    sessionRevision.current += 1;
    localStorage.setItem("touki_token", data.token);
    sessionWorkAllowed.current = true;
    setUser(data.user);
    setSessionEpoch((epoch) => epoch + 1);
    setLoading(false);
    return data.user;
  };

  const fetchMe = useCallback(async () => {
    const revision = sessionRevision.current;
    try {
      const hasSession = localStorage.getItem("touki_token") || localStorage.getItem("touki_has_session");
      if (!hasSession) {
        setUser(null);
        return;
      }
      const { data } = await api.get("/auth/me");
      if (revision === sessionRevision.current) setUser(data);
    } catch (e) {
      if (revision === sessionRevision.current) setUser(null);
    } finally {
      if (revision === sessionRevision.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password, isCurrent) => {
    return requestAuthentication("/auth/login", { email, password }, (data) => {
      // An account with a second factor gets a challenge, not a session. Storing
      // nothing here matters: half a sign-in must not leave a token behind.
      if (data.mfa_required) return { mfa_required: true, challenge: data.challenge };
      return establishSession(data);
    }, isCurrent);
  };

  /** Second step: the authenticator code, or a recovery code. */
  const completeMfa = async (challenge, code, isCurrent) => {
    return requestAuthentication("/auth/mfa/verify", { challenge, code }, establishSession, isCurrent);
  };

  const signup = async (email, password, name, isCurrent) => {
    return requestAuthentication("/auth/signup", { email, password, name }, establishSession, isCurrent);
  };

  // Google ID token verified server-side against Google's public keys. The
  // response carries the same {token, user} shape as password sign-in, so the
  // session is established exactly the same way.
  const loginWithGoogle = async (credential, isCurrent) => {
    return requestAuthentication("/auth/google/verify", { credential }, (data) => {
      if (data.mfa_required) return { mfa_required: true, challenge: data.challenge };
      return establishSession(data);
    }, isCurrent);
  };

  const loginWithGoogleSession = async (session_id, isCurrent) => {
    return requestAuthentication("/auth/google/session", { session_id }, (data) => {
      if (!data.mfa_required) setUserAfterGoogle(data.user);
      return data;
    }, isCurrent);
  };

  const changePassword = async (current_password, new_password) => {
    return requestAuthentication("/auth/change-password", { current_password, new_password }, (data) => {
      // Password rotation revokes the previous token. Keep the replacement even
      // after ordinary page navigation, but never after a newer login/logout.
      // The response contains no user: retain the current identity, and fence
      // profile requests that were made with the now-revoked credential.
      if (data?.token) {
        localStorage.setItem("touki_token", data.token);
        sessionRevision.current += 1;
        sessionWorkAllowed.current = true;
        setSessionEpoch((epoch) => epoch + 1);
      }
      return data;
    });
  };

  const logout = async () => {
    // Invalidate pending profile reads immediately, not after the network reply.
    sessionWorkAllowed.current = false;
    const revision = ++sessionRevision.current;
    let deviceId = null;
    try { deviceId = localStorage.getItem("toukky_device_id"); } catch { /* storage blocked */ }
    if (deviceId) {
      try { await api.delete(`/devices/${encodeURIComponent(deviceId)}`); } catch (e) { /* noop */ }
      // A replacement login may have installed a different token/device while
      // the old unregister request was pending. Never log that session out.
      if (revision !== sessionRevision.current) return null;
      try { localStorage.removeItem("toukky_device_id"); } catch { /* storage blocked */ }
    }
    let confirmed = true;
    try { await api.post("/auth/logout"); } catch { confirmed = false; }
    if (revision !== sessionRevision.current) return null;
    // Also invalidate profile reads begun DURING logout, not just before it.
    sessionRevision.current += 1;
    try { localStorage.removeItem("touki_token"); } catch { /* still clear the mounted session */ }
    try { localStorage.removeItem("touki_has_session"); } catch { /* still clear the mounted session */ }
    // Support-case access tokens kept in this browser (pages/SupportCase):
    // signing out must leave nothing that still opens someone's case.
    try { localStorage.removeItem("touki_support_tokens"); } catch { /* storage blocked */ }
    setUser(null);
    setLoading(false);
    // Local purge must still happen on failure; callers must not present it
    // as a confirmed server revocation. null means superseded by another login.
    return confirmed;
  };

  const setUserAfterGoogle = (u) => {
    sessionRevision.current += 1;
    // API requests prefer a bearer over the cookie. A confirmed cookie login
    // must stop sending the previous account's bearer before showing its user.
    localStorage.removeItem("touki_token");
    localStorage.setItem("touki_has_session", "1");
    sessionWorkAllowed.current = true;
    setUser(u);
    setSessionEpoch((epoch) => epoch + 1);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, completeMfa, signup, logout, fetchMe, setUserAfterGoogle, loginWithGoogle, loginWithGoogleSession, changePassword, captureSession, sessionEpoch }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
